import { useRef, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import * as Yup from 'yup';
import { Col, Row, message, Modal, Result } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Select } from 'formik-antd';
import { useQuery } from 'hooks';
import { Box, FormInput, Button } from 'atoms';
import { ACL, EditModeBtn } from 'components/common';
import { CancelButton } from 'utils/commonStyled';
import {
	LRReference,
	LRSenderDetails,
	LRReceiverDetails,
	LRVehicleDetails,
	LRGoodsSummary,
	LRGoodsDetails,
} from './components';
import { getClientList } from 'store/clientSlice';
import { getPincodeList, addUser } from 'store/pageSlice';
import { LRCopy } from '../LRView/LRview';
import { LRPrint } from '../LRView/components/LRPrint';

const LRCreateValidations = Yup.object({
	project_id: Yup.number().required().label('Project'),
	vendor_id: Yup.number().required().label('Vendor'),
	sender_name: Yup.string().required().min(3).max(128).label('Sender Name'),
	sender_address: Yup.string().required().min(3).max(200).label('Sender Address'),
	sender_pincode: Yup.string().required().min(6).max(6).label('Sender Pincode'),
	source_location: Yup.string().required(),
	receiver_name: Yup.string().required().min(3).max(128).label('Receiver Name'),
	receiver_address: Yup.string().required().min(3).max(200).label('Receiver Address'),
	receiver_pincode: Yup.string().required().min(6).max(6),
	client_document_number: Yup.string().min(3).max(32).nullable(),
	ewb_number: Yup.string()
		.nullable()
		.min(12)
		.max(12)
		.matches(/^[0-9]*$/, 'Invalid EWB. Only Numbers Allowed.')
		.label('EWB No.'),
	goods_description: Yup.string().required().min(3).max(32).label('Goods Description'),
	declared_value: Yup.number().required().label('Declared Value').nullable(),
	gross_weight: Yup.number().required().label('Gross Weight').nullable(),
	unit_count: Yup.number().required().min(1).nullable(),
	vehicle_number: Yup.string()
		.min(8)
		.max(12)
		.label('Vehicle Number')
		.nullable()
		.matches(
			/^(?:[0-9]{2}BH[0-9]{4}[A-Z]{1,2}|[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{1,4}|[A-Z]{2}[0-9]{4,8})$/,
			'Enter Valid Vehicle Number'
		),
	driver_name: Yup.string().min(3).label('Driver Name').nullable(),
});

export function LRCreationForm() {
	const dispatch = useDispatch();
	const [LRData, setLRData] = useState([]);
	const LRPrintRef = useRef();
	const { id } = useParams();
	const [isEditMode, setIsEditMode] = useState(true);
	const [viewSuccess, setViewSuccess] = useState(false);
	const [isModalOpen, setModalOpen] = useState(false);
	const [printVisible, setPrintVisible] = useState(false);
	const [projects, setProjects] = useState();
	const [vendors, setVendors] = useState();
	const [formData, setFormData] = useState({ unit_count: 1 });
	const primaryForm = useRef();
	const goodsForm = useRef();
	const [query] = useQuery();
	const navigate = useNavigate();

	function OpenLRViewModal() {
		setViewSuccess(false);
		setModalOpen(true);
	}

	function onClose() {
		setModalOpen(false);
		navigate(`/lr`);
	}

	const verifyAndProcess = () => {
		primaryForm.current.submitForm();
		if (!primaryForm.current.isValid) {
			const primaryFormErrors = primaryForm.current.errors;
			const firstError = Object.keys(primaryFormErrors)[0];
			message.error(primaryFormErrors[firstError]);
			return false;
		}
		goodsForm.current.submitForm();
		const goodsFormErrors = goodsForm.current.isValid;
		if (!goodsFormErrors) {
			message.error('Missing fields in Goods Details');
			return false;
		}
		const { goods_details } = goodsForm.current.values;
		const data = { ...primaryForm.current.values, goods_details };
		setLRData(data);
		const payload = { data };
		if (id) {
			payload.update_type = 'consignment_note';
			dispatch(addUser({ data: payload, isEditMode })).then((res) => {
				setViewSuccess(true);
				setLRData(res?.data);
			});
		} else {
			payload.create_type = 'consignment_note';
			dispatch(addUser({ data: payload })).then((res) => {
				setViewSuccess(true);
				setLRData(res?.data);
			});
		}
		setViewSuccess(true);
		return true;
	};

	const printComponent = useReactToPrint({
		content: () => LRPrintRef.current,
		onAfterPrint: () => onClose(),
		pageStyle: '@page { size: 42cm 56cm }',
	});

	useEffect(() => {
		if (printVisible) {
			printComponent();
			setPrintVisible(false);
		}
	}, [printVisible, printComponent]);

	function handlePrint() {
		setPrintVisible(true);
	}

	useEffect(() => {
		if (query.get('isEdit') === 'false') {
			setIsEditMode(false);
		} else {
			setIsEditMode(true);
		}
	}, [query]);

	useEffect(() => {
		dispatch(
			getClientList({
				search_type: 'project',
				filter_by: {},
			})
		).then((res) => {
			setProjects(
				res?.data?.map((item) => ({
					label: item?.project_location_code,
					value: item?.id,
					id: item?.id,
					project_loc_codes: item?.project_loc_codes,
					item,
				}))
			);
		});
		dispatch(
			getClientList({
				search_type: 'list_vendor',
				filter_by: {},
			})
		).then((res) => {
			setVendors(
				res?.data?.map((item) => ({
					label: `${item?.vendor_name} - (${item?.username})`,
					value: item?.id,
					id: item?.id,
					item,
				}))
			);
		});
		if (id) {
			dispatch(
				getClientList({
					search_type: 'consignment_note',
					filter_by: {
						id,
					},
				})
			).then((res) => {
				setFormData(res?.data?.[0]);
			});
		}
	}, [dispatch, id]);

	function handleEdit() {
		setIsEditMode(true);
		navigate(`/lr/${id}?isEdit=true`);
	}

	function setlocation(pin_code, locType) {
		const pincode = String(pin_code);
		if (pincode?.length === 6) {
			dispatch(getPincodeList(pincode))
				.then((res) => {
					if (locType === 'source') {
						primaryForm.current?.setFieldValue('source_location', res.rows[0].city);
					} else {
						primaryForm.current?.setFieldValue(
							'destination_location',
							res.rows[0].city
						);
					}
				})
				.catch(() => {
					if (locType === 'source') {
						primaryForm.current?.setFieldValue('source_location', null);
					} else {
						primaryForm.current?.setFieldValue('destination_location', null);
					}
				});
		}
	}

	return (
		<>
			<Formik
				initialValues={formData}
				validationSchema={LRCreateValidations}
				enableReinitialize
				innerRef={primaryForm}
				validateOnMount
				onSubmit={() => null}
			>
				{() => (
					<Box>
						{id && (
							<ACL action="update" module="consignment_note">
								<Row gutter={16} justify="space-between">
									<div></div>
									<EditModeBtn handleEdit={handleEdit} />
								</Row>
							</ACL>
						)}
						<Form className={query.get('isEdit') === 'false' ? 'disabled-div' : ''}>
							<Box padding="1.5rem 2rem" background="#fff">
								<Row gutter={16} justify="space-between">
									<Col lg={12}>
										<FormInput name="project_id" label="Project *">
											<Select
												showSearch
												disabled={id}
												name="project_id"
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
												options={projects}
												placeholder="Project"
											/>
										</FormInput>
									</Col>
									<Col lg={12}>
										<FormInput name="vendor_id" label="Vendor *">
											<Select
												name="vendor_id"
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
												showSearch
												options={vendors}
												placeholder="Vendor"
												disabled={!isEditMode}
											/>
										</FormInput>
									</Col>
								</Row>
								<Row gutter={12}>
									<Col span={12}>
										<LRSenderDetails setSource={setlocation} isEditMode />
									</Col>
									<Col span={12}>
										<LRReceiverDetails
											setDestination={setlocation}
											isEditMode
										/>
									</Col>
								</Row>
								<Row>
									<Col span={12}>
										<Row>
											<Col span={12}>
												<LRReference isEditMode={isEditMode} />
											</Col>
											<Col span={12}>
												<LRVehicleDetails isEditMode={isEditMode} />
											</Col>
										</Row>
									</Col>
									<Col span={12}>
										<LRGoodsSummary isEditMode={isEditMode} />
									</Col>
								</Row>
							</Box>
						</Form>
						<Row>
							<Col span={24}>
								<LRGoodsDetails
									formRef={goodsForm}
									formdata={formData}
									isEditMode={isEditMode}
								/>
							</Col>
						</Row>
						{query.get('isEdit') !== 'false' && (
							<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
								<CancelButton onClick={() => navigate('/lr')} type="button">
									Cancel
								</CancelButton>
								<Button
									borderRadius="4px"
									backgroundColor="#18405d"
									onClick={verifyAndProcess}
								>
									{id ? 'Update' : 'Submit'}
								</Button>
							</Box>
						)}
					</Box>
				)}
			</Formik>
			<Modal
				open={isModalOpen || viewSuccess}
				width={viewSuccess ? 500 : 1500}
				centered
				maskClosable={false}
				onCancel={onClose}
				footer={
					viewSuccess ? null : (
						<div>
							<Button key="submit" type="primary" onClick={handlePrint}>
								Print
							</Button>
						</div>
					)
				}
			>
				{viewSuccess && (
					<Result
						status="success"
						title={`Consignment Note ${id ? 'edited' : 'created'}`}
						extra={[
							<Button type="primary" key="print" onClick={OpenLRViewModal}>
								Print
							</Button>,
						]}
					/>
				)}
				{isModalOpen && (
					<>
						<div style={{ overflow: 'scroll', height: '80vh' }}>
							<LRCopy details={LRData} />
						</div>
						{console.log(printVisible)}
						<LRPrint details={LRData} ref={LRPrintRef} isvisible={printVisible} />
					</>
				)}
			</Modal>
		</>
	);
}
